import axios from 'axios';

// Use relative URL in production so cookies work across all domains (fultonzone.com, footiepicks.com)
// In development, use absolute URL to localhost
const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'  // Relative URL - same domain as frontend
  : (process.env.REACT_APP_API_URL || 'http://localhost:8181/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
  timeout: 30000, // 30 second timeout for all requests
});

// CSRF token storage
let csrfToken = null;

// Fetch CSRF token on initialization
export const fetchCsrfToken = async () => {
  try {
    const response = await api.get('/csrf-token');
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
};

// Add CSRF token to non-GET requests
api.interceptors.request.use(async (config) => {
  // Skip CSRF token for auth routes that don't need it
  const skipCsrfRoutes = [
    '/auth/login',
    '/auth/register',
    '/auth/accept-invitation',
    '/auth/verify-email',
    '/auth/resend-verification',
    '/auth/forgot-password',
    '/auth/reset-password'
  ];

  const shouldSkipCsrf = skipCsrfRoutes.some(route => config.url?.includes(route));

  // Only add CSRF token for state-changing methods (excluding auth routes)
  if (!['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase()) && !shouldSkipCsrf) {
    // Fetch CSRF token if we don't have one
    if (!csrfToken) {
      try {
        await fetchCsrfToken();
      } catch (error) {
        console.error('Could not get CSRF token');
      }
    }
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }
  }
  return config;
});

// Handle 401 errors (unauthorized) and CSRF token errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if not already on auth pages
      const currentPath = window.location.pathname;
      const authPages = ['/login', '/register', '/accept-invitation', '/verify-email', '/reset-password', '/forgot-password'];

      if (!authPages.includes(currentPath) && !currentPath.startsWith('/auth')) {
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403 && error.response?.data?.message?.includes('CSRF')) {
      // CSRF token invalid - refetch and retry
      try {
        await fetchCsrfToken();
        const originalRequest = error.config;
        originalRequest.headers['x-csrf-token'] = csrfToken;
        return api(originalRequest);
      } catch (retryError) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  acceptInvitation: (data) => api.post('/auth/accept-invitation', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get(`/auth/validate-reset-token?token=${encodeURIComponent(token)}`),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getCurrentUser: () => api.get('/auth/me'),
};

// Picks API
export const picksAPI = {
  getCurrentRound: () => api.get('/picks/current-round'),
  getRoundPicks: (roundNumber) => api.get(`/picks/round/${roundNumber}`),
  getRoundHeartbeat: (roundNumber) => api.get(`/picks/round/${roundNumber}/heartbeat`),
  saveRoundPicks: (roundNumber, picks) => api.post(`/picks/round/${roundNumber}`, { picks }),
  getHistory: () => api.get('/picks/history'),
  getUserHistory: (userId) => api.get(`/picks/user/${userId}/history`),
};

// Matches API
export const matchesAPI = {
  getRound: (roundNumber) => api.get(`/matches/round/${roundNumber}`),
  getMatch: (matchId) => api.get(`/matches/${matchId}`),
  getTeamMatches: (teamId) => api.get(`/matches/team/${teamId}`),
};

// Leaderboard API
export const leaderboardAPI = {
  getRoundStandings: (roundNumber) => api.get(`/leaderboard/round/${roundNumber}/standings`), // User standings with round + season totals
  getTeamStandings: () => api.get('/leaderboard/standings'), // NRL team ladder
};

// Admin API
export const adminAPI = {
  getUserRoundPicks: (userId, roundNumber) => api.get(`/admin/picks/user/${userId}/round/${roundNumber}`),
  editPick: (pickId, data) => api.put(`/admin/picks/${pickId}`, data),
  createPickForUser: (data) => api.post('/admin/picks', data),
  batchSavePicksForUser: (data) => api.post('/admin/picks/batch', data),
  markMatchLive: (matchId) => api.post(`/admin/matches/${matchId}/mark-live`),
  markMatchFinished: (matchId) => api.post(`/admin/matches/${matchId}/mark-finished`),
  cancelMatch: (matchId) => api.post(`/admin/matches/${matchId}/cancel`),
  enterScores: (matchId, data) => api.put(`/admin/matches/${matchId}/scores`, data),
  batchEnterScores: (scores) => api.post('/admin/matches/batch-scores', { scores }),
  changeVenue: (matchId, data) => api.put(`/admin/matches/${matchId}/venue`, data),
  getAuditLog: (limit = 50, offset = 0) => api.get('/admin/audit', { params: { limit, offset } }),
  getAllUsers: (seasonId) => {
    const params = seasonId ? { seasonId } : {};
    return api.get('/admin/users', { params });
  },
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  sendPasswordReset: (userId, body = {}) => api.post(`/admin/users/${userId}/reset-password`, body),
  getResetLink: (userId) => api.post(`/admin/users/${userId}/reset-link`),
  resendVerification: (userId) => api.post(`/admin/users/${userId}/resend-verification`),
  refreshTeamStandings: () => api.post('/admin/standings/refresh'),
  getLockedAccounts: () => api.get('/admin/locked-accounts'),
  unlockAccount: (userId) => api.post(`/admin/users/${userId}/unlock`),
  getSecurityEvents: (filters) => api.get('/admin/security-events', { params: filters }),
  getUserSecurityEvents: (userId, limit = 50, offset = 0) => api.get(`/admin/security-events/user/${userId}`, { params: { limit, offset } }),
  sendInvitation: (email, role) => api.post('/admin/invitations', { email, role }),
  getInvitations: () => api.get('/admin/invitations'),
  resendInvitation: (invitationId) => api.post(`/admin/invitations/${invitationId}/resend`),
  revokeInvitation: (invitationId) => api.delete(`/admin/invitations/${invitationId}`),
};

// Season API
export const seasonAPI = {
  getAllSeasons: () => api.get('/admin/seasons'),
  createSeason: (data) => api.post('/admin/seasons', data),
  setActiveSeason: (seasonId) => api.put(`/admin/seasons/${seasonId}/set-active`),
  getParticipants: (seasonId) => api.get(`/admin/seasons/${seasonId}/participants`),
  addParticipant: (seasonId, userId) => api.post(`/admin/seasons/${seasonId}/participants`, { userId }),
  addParticipantsBulk: (seasonId, userIds) => api.post(`/admin/seasons/${seasonId}/participants/bulk`, { userIds }),
  removeParticipant: (seasonId, userId) => api.delete(`/admin/seasons/${seasonId}/participants/${userId}`),
  optOutParticipant: (seasonId, userId, reason) => api.put(`/admin/seasons/${seasonId}/participants/${userId}/opt-out`, { reason }),
  optInParticipant: (seasonId, userId) => api.put(`/admin/seasons/${seasonId}/participants/${userId}/opt-in`),
  getSeasonTeams: (seasonId) => api.get(`/admin/seasons/${seasonId}/teams`),
  updateSeasonTeams: (seasonId, teamIds) => api.put(`/admin/seasons/${seasonId}/teams`, { teamIds }),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  markSettingsVisited: () => api.post('/users/mark-settings-visited'),
  markNotificationPromptSeen: () => api.post('/users/mark-notification-prompt-seen'),
  recordNotificationPromptShown: () => api.post('/users/record-notification-prompt-shown'),
  generatePasswordResetToken: () => api.post('/users/generate-password-reset-token'),
};

// Teams API
export const teamsAPI = {
  getAll: () => api.get('/teams'),
};

export default api;
